import { useEffect, useState, useRef } from "react";
import { ShoppingCart, Check, Truck, Store as StoreIcon, ImagePlus, X, Loader2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { api, resolveImg } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const ProductCard = ({ product, index }) => {
  const { add } = useCart();
  const [size, setSize] = useState(product.sizes?.[0] || "");
  const [color, setColor] = useState(product.colors?.[0] || "");
  const [pers, setPers] = useState("");
  const [persImg, setPersImg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const gallery = product.images?.length ? product.images : (product.img ? [product.img] : []);
  const photoLabel = (idx) => (product.image_labels?.[idx] || ["front", "back", "measurements"][idx] || `view ${idx + 1}`).toUpperCase();
  const sizeUp = (s) => Number(product.size_upcharges?.[s] || 0);
  const fmtUp = (u) => `+$${Number(u) % 1 === 0 ? Number(u).toFixed(0) : Number(u).toFixed(2)}`;
  const [mainImg, setMainImg] = useState(gallery[0]);
  const soldOut = product.track_inventory && (product.stock || 0) <= 0;
  const lowStock = product.track_inventory && (product.stock || 0) > 0 && (product.stock || 0) <= 5;
  const showPreview = product.personalization && !soldOut && (pers || persImg);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPersImg(data.url);
      toast.success("Photo added");
    } catch {
      toast.error("Couldn't upload that image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const addToCart = () => {
    if (soldOut) return;
    if (product.sizes?.length && !size) { toast.error("Please pick a size."); return; }
    if (product.colors?.length && !color) { toast.error("Please pick a color."); return; }
    add(product, size, color, 1, pers, persImg);
    toast.success(`${product.name} added to cart`);
    setPers("");
    setPersImg("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div className="bg-white border border-line flex flex-col h-full group" data-testid={`store-product-${product.id}`}>
        <div className="relative aspect-square overflow-hidden bg-[#e9e6dd]">
          <img src={resolveImg(mainImg)} alt={product.name} className={`w-full h-full object-cover transition-transform duration-700 ${soldOut ? "grayscale opacity-70" : "group-hover:scale-105"}`} />
          {showPreview && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none p-6" data-testid={`pers-preview-${product.id}`}>
              {persImg && (
                <img src={resolveImg(persImg)} alt="Your design" className="max-h-[45%] max-w-[55%] object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]" />
              )}
              {pers && (
                <span className="mt-2 px-3 py-1 font-display tracking-[0.06em] text-2xl text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)] text-center break-words max-w-full">
                  {pers}
                </span>
              )}
            </div>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-ink/85 text-white text-[10px] tracking-[0.1em] px-2.5 py-1">
            {product.fulfillment === "ship" ? <><Truck size={11} /> SHIPS</> : <><StoreIcon size={11} /> PICKUP</>}
          </div>
          {showPreview && (
            <div className="absolute bottom-3 left-3 bg-gold/90 text-ink text-[10px] font-bold tracking-[0.1em] px-2.5 py-1">
              YOUR PREVIEW
            </div>
          )}
          {soldOut && (
            <div className="absolute inset-0 flex items-center justify-center" data-testid={`sold-out-${product.id}`}>
              <span className="bg-ink text-[#f2f0ea] text-xs font-bold tracking-[0.22em] px-5 py-2">SOLD OUT</span>
            </div>
          )}
          {lowStock && (
            <div className="absolute top-3 right-3 bg-gold text-ink text-[10px] font-bold tracking-[0.08em] px-2.5 py-1" data-testid={`low-stock-${product.id}`}>
              ONLY {product.stock} LEFT
            </div>
          )}
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-3 px-5 pt-4" data-testid={`gallery-thumbs-${product.id}`}>
            {gallery.slice(0, 6).map((url, idx) => (
              <div key={url} className="flex flex-col items-center gap-1">
                <button type="button" onClick={() => setMainImg(url)} data-testid={`gallery-thumb-${product.id}-${idx}`}
                  className={`w-12 h-12 border overflow-hidden transition-colors ${mainImg === url ? "border-ink" : "border-line hover:border-navy/50"}`}>
                  <img src={resolveImg(url)} alt={`${photoLabel(idx)} photo`} className="w-full h-full object-cover" />
                </button>
                <span className="text-[8px] tracking-[0.12em] text-navy/50 font-semibold">{photoLabel(idx)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-display text-xl tracking-[0.02em] text-ink leading-tight">{product.name}</h3>
          {product.description && <p className="text-navy/60 text-sm mt-2 leading-relaxed">{product.description}</p>}
          <div className="text-lg font-bold text-ink mt-3">{money(product.price + sizeUp(size))}</div>

          {product.sizes?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] tracking-[0.14em] text-navy/50 font-semibold mb-1.5">SIZE</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`size-group-${product.id}`}>
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${product.id}-${s}`} disabled={soldOut}
                    className={`min-w-[38px] px-2 py-1.5 text-xs border transition-colors disabled:opacity-40 ${size === s ? "bg-ink text-white border-ink" : "border-line text-navy/70 hover:border-navy"}`}>
                    {s}{sizeUp(s) ? <span className="text-gold font-semibold"> {fmtUp(sizeUp(s))}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] tracking-[0.14em] text-navy/50 font-semibold mb-1.5">COLOR</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`color-group-${product.id}`}>
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${product.id}-${c}`} disabled={soldOut}
                    className={`px-3 py-1.5 text-xs border transition-colors flex items-center gap-1 disabled:opacity-40 ${color === c ? "bg-navy text-white border-navy" : "border-line text-navy/70 hover:border-navy"}`}>
                    {color === c && <Check size={11} />}{c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.personalization && !soldOut && (
            <div className="mt-4" data-testid={`pers-wrap-${product.id}`}>
              <p className="text-[11px] tracking-[0.14em] text-navy/50 font-semibold mb-1.5">
                {(product.personalization_label || "Personalization").toUpperCase()}
                {product.personalization_fee > 0 && <span className="text-gold"> (+${Number(product.personalization_fee).toFixed(2)})</span>}
              </p>
              <input value={pers} onChange={(e) => setPers(e.target.value.slice(0, 60))} data-testid={`pers-input-${product.id}`}
                placeholder="Add a name or text (optional)"
                className="w-full border border-line px-3 py-2 text-sm outline-none focus:border-navy bg-paper" />
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid={`pers-file-${product.id}`} />
              {persImg ? (
                <div className="flex items-center gap-2 mt-2 border border-line p-2 bg-paper">
                  <img src={resolveImg(persImg)} alt="Your upload" className="w-10 h-10 object-cover" />
                  <span className="text-xs text-navy/60 flex-1">Photo added</span>
                  <button onClick={() => { setPersImg(""); if (fileRef.current) fileRef.current.value = ""; }} data-testid={`pers-remove-${product.id}`} className="text-navy/40 hover:text-red-600"><X size={15} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`pers-upload-${product.id}`}
                  className="mt-2 w-full flex items-center justify-center gap-2 border border-dashed border-navy/30 text-navy/60 py-2 text-xs hover:border-navy hover:text-ink transition-colors disabled:opacity-60">
                  {uploading ? <><Loader2 size={14} className="animate-spin" /> UPLOADING…</> : <><ImagePlus size={14} /> UPLOAD A PHOTO / LOGO</>}
                </button>
              )}
            </div>
          )}

          <button onClick={addToCart} data-testid={`add-to-cart-${product.id}`} disabled={soldOut}
            className="mt-5 w-full bg-gold text-ink py-3 text-xs font-bold tracking-[0.16em] hover:bg-gold-soft transition-colors flex items-center justify-center gap-2 mt-auto disabled:bg-navy/20 disabled:text-navy/50 disabled:cursor-not-allowed">
            <ShoppingCart size={15} /> {soldOut ? "SOLD OUT" : "ADD TO CART"}
          </button>
        </div>
      </div>
    </Reveal>
  );
};

export default function SpiritStore() {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const storeName = settings.store_name || "Spirit Store";

  useEffect(() => {
    Promise.all([api.get("/store/products"), api.get("/store/groups")])
      .then(([p, g]) => {
        setProducts(p.data);
        setGroups(g.data);
        if (g.data.length) {
          const wanted = (searchParams.get("shop") || "").toLowerCase();
          const match = g.data.find((gr) => gr.name.toLowerCase() === wanted);
          let best = null, bestCount = 0;
          for (const gr of g.data) {
            const n = p.data.filter((pr) => pr.store === gr.name).length;
            if (n > bestCount) { best = gr.name; bestCount = n; }
          }
          if (wanted === "general") setActiveGroup("General");
          else if (match) setActiveGroup(match.name);
          else if (best) setActiveGroup(best);
          else if (p.data.some((pr) => !pr.store)) setActiveGroup("General");
          else setActiveGroup(g.data[0].name);
        }
      })
      .catch(() => { setProducts([]); setGroups([]); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasGroups = groups.length > 0;
  const tabs = hasGroups
    ? [...groups.map((g) => g.name), ...(products.some((p) => !p.store) ? ["General"] : [])]
    : [];
  const visible = hasGroups
    ? products.filter((p) => (activeGroup === "General" ? !p.store : p.store === activeGroup))
    : products;
  const shopTitle = hasGroups && activeGroup && activeGroup !== "General" ? activeGroup : storeName;

  const selectGroup = (name) => {
    setActiveGroup(name);
    setSearchParams({ shop: name === "General" ? "general" : name }, { replace: true });
  };

  return (
    <div data-testid="spirit-store-page">
      <PageHero eyebrow="SHOP THE COLLECTION" title={shopTitle.toUpperCase()} script="show your spirit" />
      <section className="py-16 bg-paper min-h-[50vh]">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          {settings.store_active === false ? (
            <div className="bg-white border border-line p-16 text-center" data-testid="store-closed">
              <p className="font-display text-3xl tracking-[0.02em] text-ink">THE STORE IS CLOSED</p>
              <p className="text-navy/60 text-sm mt-3 max-w-md mx-auto">Our online store is taking a short break. Please check back soon — or reach out for a custom quote in the meantime.</p>
              <a href="/quote" data-testid="store-closed-quote-cta" className="inline-block mt-7 bg-ink text-[#f2f0ea] px-8 py-3.5 text-xs font-bold tracking-[0.16em] hover:bg-navy transition-colors">GET A QUOTE</a>
            </div>
          ) : (
            <>
              <p className="text-navy/70 text-sm max-w-xl mb-10">
                Grab your team gear and spirit wear below. Pay securely online — pickup at school or ship right to your door.
              </p>
              {!loading && hasGroups && (
                <div className="flex flex-wrap gap-2 mb-10" data-testid="store-group-tabs">
                  {tabs.map((name) => (
                    <button key={name} onClick={() => selectGroup(name)} data-testid={`store-tab-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`px-5 py-2.5 text-xs font-bold tracking-[0.12em] border transition-colors ${activeGroup === name ? "bg-ink text-[#f2f0ea] border-ink" : "border-line text-navy/70 hover:border-navy bg-white"}`}>
                      {name.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              {loading ? (
                <p className="text-navy/50">Loading the store…</p>
              ) : products.length === 0 && !hasGroups ? (
                <div className="bg-white border border-line p-16 text-center text-navy/60">
                  The store is being stocked. Check back soon!
                </div>
              ) : visible.length === 0 ? (
                <div className="bg-white border border-line p-16 text-center text-navy/60" data-testid="store-tab-empty">
                  {activeGroup ? `${activeGroup} gear is coming soon — check back!` : "The store is being stocked. Check back soon!"}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-grid">
                  {visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
